import { Component, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';

import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { tap, debounceTime, filter } from 'rxjs/operators';

import { ChannelFollower } from '../../../../../models/influencer/channel-follower';
import { NumberUtils } from '../../../../utils/number.utils';
import { UserService } from '../../../../services/user.service';
import { chartConfig } from '../../../../app.constants';

@Component({
    selector: 'follower-by-channel',
    templateUrl: './follower-by-channel.component.html',
    styleUrls: ['./follower-by-channel.component.scss']
})
export class FollowerByChannelComponent implements OnDestroy {
    private _changeSubscription: Subscription;

    public hasNoData = false;
    public readyToShow = false;
    public chartData: any[];

    private readonly _selectedColor: string;

    public chartConfig = {
        spacingTop: 0,
        spacingRight: 0
    };

    public overridenPiePlotOptions = {
        borderWidth: 0
    };

    public legend = {
        useHTML: true,
        y: 0,
        navigationActiveColor: '#445166',
        navigationInactiveColor: 'rgba(68,81,102,0.50)',
        navigationArrowSize: 10,
        itemHiddenStyle: { color: 'rgba(68, 81, 102, 0.05)' },
        x: 87,
        align: 'center',
        floating: true
    };

    public readonly placeholderData = [
        {
            name: 'Instagram',
            y: 0.2,
            color: 'rgba(149,160,177, 0.1)',
            customLegendDisplay: '- K'
        },
        {
            name: 'YouTube',
            y: 0.2,
            color: 'rgba(149,160,177, 0.1)',
            customLegendDisplay: '- K'
        },
        {
            name: 'Blogs',
            y: 0.2,
            color: 'rgba(149,160,177, 0.1)',
            customLegendDisplay: '- K'
        },
        {
            name: 'Tiktok',
            y: 0.2,
            color: 'rgba(149,160,177, 0.1)',
            customLegendDisplay: '- K'
        }
    ];

    private _channelFollowersSubject: Subject<ChannelFollower[]>;
    @Input() set channelFollowers(value: ChannelFollower[]) {
        this._channelFollowersSubject.next(value);
    }

    private _selectedChannelIdSubject: Subject<number>;
    @Input() set selectedChannelId(value: number) {
        this._selectedChannelIdSubject.next(value);
    }

    @Input() isFetchingData: boolean;

    constructor(cd: ChangeDetectorRef, userService: UserService) {
        this._selectedColor = userService.chartColors[0];
        this._channelFollowersSubject = new Subject<ChannelFollower[]>();
        this._selectedChannelIdSubject = new Subject<number>();

        this._changeSubscription = combineLatest([
            this._channelFollowersSubject,
            this._selectedChannelIdSubject
        ]).pipe(
            filter(([channelFollowers, selectedChannelId]) => channelFollowers !== undefined && selectedChannelId !== undefined),
            tap(() => {
                this.readyToShow = false;
                cd.detectChanges();
            }),
            debounceTime(100)
            ).subscribe({
                next: ([channelFollowers, selectedChannelId]) => {
                    this.readyToShow = true;

                    if (!Array.isArray(channelFollowers)
                        || channelFollowers.length === 0
                        || !channelFollowers.find(c => c.channelId === selectedChannelId)) {
                        this.hasNoData = true;
                        return;
                    }

                    this.hasNoData = false;
                    this.chartData = this.getChartData(channelFollowers, selectedChannelId);
                }
            });
    }

    ngOnDestroy(): void {
        this._changeSubscription.unsubscribe();
    }

    private getChartData(channelFollowers: ChannelFollower[], selectedChannelId: number) {
        let drawnChannelFollowers = channelFollowers;
        const selectedChannelFollower = channelFollowers.find(c => c.channelId === selectedChannelId);

        if (selectedChannelFollower) {
            const others = channelFollowers.filter(x => x !== selectedChannelFollower);
            drawnChannelFollowers = [selectedChannelFollower, ...others];
        }

        const unselectedColorDelta = drawnChannelFollowers[0].channelId === selectedChannelId ? 1 : 0;
        const unSelectedColors = chartConfig.unselectedColors;

        return drawnChannelFollowers.map((c, i) => {
            return {
                name: c.channelName,
                y: c.followerCount,
                customLegendDisplay: NumberUtils.normalizeNumberToString(c.followerCount),
                color: c.channelId === selectedChannelId
                    ? this._selectedColor
                    : unSelectedColors[(i - unselectedColorDelta) % unSelectedColors.length]
            };
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer/influencer-insight/follower-by-channel/follower-by-channel.component.ts